import React from 'react'

import { Plus, MapPin, User, /* Key, */ Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import addIcon from '@/assets/images/icons/add.png'
import { PaginatedResponse } from '@/core/api/dtos/common/types'
import { Location } from '@/core/api/entities/location'
import { PERMISSIONS } from '@/core/api/services/permissionService'
import { useOwnCountries } from '@/core/hooks/misc/useCountries'
import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import { useLocationCreate } from '@/features/zones/hooks/useLocationCreate'
import { useLocationDeletion } from '@/features/zones/hooks/useLocationDeletion'
// import { useLocationShare } from '@/features/zones/hooks/useLocationShare'
import { useLocationUpdate } from '@/features/zones/hooks/useLocationUpdate'
import DeleteLocationModal from '@/features/zones/modals/DeleteLocationModal'
// import ShareLocationModal from '@/features/zones/modals/ShareLocationModal'
import UpdateLocationModal from '@/features/zones/modals/UpdateLocationModal'
import { AccountBadge } from '@/shared/components/atoms/AccountBadge/AccountBadge'
import { Button } from '@/shared/components/atoms/Button'
import { AccountFilterDropdown } from '@/shared/components/molecules/AccountFilterDropdown/AccountFilterDropdown'
import ActionDropdownMenu, {
	ActionMenuItem,
} from '@/shared/components/molecules/ActionDropdownMenu'
import { Card } from '@/shared/components/molecules/Card/Card'
import { PaginationControls } from '@/shared/components/molecules/PaginationControls/PaginationControls'
import { Search } from '@/shared/components/molecules/Search/Search'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'
import { getCountryFlag } from '@/shared/utils/countryFlags'

interface LocationTabProps {
	locations: PaginatedResponse<Location> | undefined
	isLoading: boolean
	error: Error | null
	searchTerm: string
	setSearchTerm: (value: string) => void
	pagination: {
		current: number
		pageSize: number
		onChange: (page: number) => void
		onShowSizeChange: (current: number, size: number) => void
		showQuickJumper: boolean
		showSizeChanger: boolean
	}
	refetch: () => void
	accountFilter: number | null
	setAccountFilter: (id: number | null) => void
	showAccountFilter: boolean
}

export const LocationTab: React.FC<LocationTabProps> = ({
	locations,
	isLoading,
	error,
	searchTerm,
	setSearchTerm,
	pagination,
	refetch,
	accountFilter,
	setAccountFilter,
	showAccountFilter,
}) => {
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const navigate = useNavigate()
	const currentAccount = useCurrentAccount()
	const { hasAnyPermission } = usePermission()
	const canCreateLocation = hasAnyPermission([
		PERMISSIONS.CREATE_USER_STORES,
		PERMISSIONS.MANAGE_CHILDREN_PLAYERS,
		PERMISSIONS.MANAGE_OWN_LEVEL_PLAYERS,
	])
	const canDeleteLocation = hasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_PLAYERS,
		PERMISSIONS.MANAGE_OWN_LEVEL_PLAYERS,
		PERMISSIONS.DESTROY_USER_STORES,
	])

	// Fetch countries for filter
	const { data: _countries = [] } = useOwnCountries()

	// Location deletion hook
	const locationDeletion = useLocationDeletion({
		onSuccess: () => {
			refetch() // Refetch locations after successful deletion
		},
	})

	// Location sharing hook
	/* const locationShare = useLocationShare({
		onSuccess: () => {
			refetch() // Refetch locations after successful sharing
		},
	}) */

	// Location update hook
	const locationUpdate = useLocationUpdate(() => {
		refetch() // Refetch locations after successful update
	})

	// Location create hook
	const locationCreate = useLocationCreate(() => {
		refetch() // Refetch locations after successful creation
	})

	const handleCreateLocation = () => {
		locationCreate.openCreateModal()
	}

	const handlePageSizeChange = (newSize: number) => {
		const currentFirstItemIndex = (pagination.current - 1) * pagination.pageSize
		const newPage = Math.floor(currentFirstItemIndex / newSize) + 1
		pagination.onShowSizeChange(newPage, newSize)
	}

	const renderLocationCard = (location: Location) => {
		const zoneCount = location.zones?.length || 0

		const menuItems: ActionMenuItem[] = [
			/* {
				label: t('location.list.shareLocationAccess'),
				icon: Key,
				onClick: () => locationShare.openShareModal(location),
				variant: 'default',
			}, */
			...(canDeleteLocation
				? [
						{
							label: t('location.list.remove'),
							icon: Trash2,
							onClick: () => locationDeletion.openDeleteModal(location),
							variant: 'destructive' as const,
						},
					]
				: []),
		]

		return (
			<Card
				key={location.id}
				className="overflow-hidden cursor-pointer transition-all hover:shadow-lg group p-6 bg-card-dark border-border relative"
				onClick={() => {
					navigate(`/locations/${location.id}`)
				}}
			>
				<div className="absolute top-4 right-4 z-10">
					<ActionDropdownMenu items={menuItems} />
				</div>

				<div className="space-y-4">
					<div className="flex items-start justify-between">
						<div className="flex-1 min-w-0 pr-10">
							{location.accountName &&
								location.accountId !== currentAccount?.id && (
									<div className="mb-2">
										<AccountBadge accountName={location.accountName} />
									</div>
								)}
							<div className="text-sm text-muted-foreground mb-2">
								{zoneCount}{' '}
								{zoneCount !== 1
									? tCommon('entities.zone.main_tabs.zones')
									: t('card.zone')}
							</div>
							<h4 className="font-normal text-xl line-clamp-1">
								{location.name}
							</h4>
						</div>
					</div>

					<div className="flex items-center gap-4">
						<div className="flex items-center gap-2 text-sm text-muted-foreground flex-1 min-w-0">
							{location.country && (
								<div className="flex-shrink-0">
									{getCountryFlag(location.country)}
								</div>
							)}
							<span className="truncate">{location.address || '-'}</span>
						</div>

						{location.manager && (
							<div className="flex items-center gap-2 text-sm text-muted-foreground flex-1 min-w-0">
								<User className="h-4 w-4 flex-shrink-0" />
								<span className="truncate">{location.manager}</span>
							</div>
						)}
					</div>
				</div>
			</Card>
		)
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<div className="flex items-center gap-3 flex-1">
					<Search
						value={searchTerm}
						onChange={setSearchTerm}
						placeholder={tCommon('entities.location.search_placeholder')}
					/>
					{showAccountFilter && (
						<div className="hidden lg:block">
							<AccountFilterDropdown
								value={accountFilter}
								onChange={setAccountFilter}
							/>
						</div>
					)}
				</div>
				<div className="hidden md:flex items-center gap-3">
					{canCreateLocation && (
						<Button
							variant="action"
							size="action"
							onClick={handleCreateLocation}
						>
							{t('location.list.createLocation')}
							<Plus className="h-4 w-4" />
						</Button>
					)}
				</div>
				<div className="md:hidden flex items-center gap-2.5 justify-end"></div>
			</div>

			{isLoading ? (
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					{Array.from({ length: 8 }).map((_, index) => (
						<Card key={index} className="overflow-hidden border">
							<div className="p-6 space-y-4">
								<div className="h-4 bg-muted rounded animate-pulse" />
								<div className="h-6 bg-muted rounded animate-pulse w-2/3" />
								<div className="h-3 bg-muted rounded animate-pulse w-1/2" />
								<div className="h-3 bg-muted rounded animate-pulse w-1/2" />
							</div>
						</Card>
					))}
				</div>
			) : error ? (
				<div className="p-6 border rounded-xl border-dashed bg-red-50">
					<div className="flex items-center gap-4">
						<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100">
							<MapPin className="h-6 w-6 text-red-500" />
						</div>
						<div className="flex-1">
							<h3 className="text-lg font-semibold mb-1 text-red-900">
								{t('location.list.errorLoading')}
							</h3>
							<p className="text-sm text-red-700">
								{t('location.list.errorDescription')}
							</p>
						</div>
					</div>
				</div>
			) : !locations?.data || locations.data.length === 0 ? (
				<div className="flex flex-col items-center justify-center px-6 bg-card-dark rounded-2xl min-h-[calc(100vh-280px)]">
					<div className="text-center space-y-4 max-w-md">
						<h3 className="text-2xl font-semibold">
							{t('location.list.empty.title')}
						</h3>
						<p className="text-sm text-muted-foreground">
							{t('location.list.empty.description')}
						</p>
						{canCreateLocation && (
							<Button
								variant="default"
								className="rounded-full bg-white text-black hover:bg-gray-100"
								onClick={handleCreateLocation}
							>
								{t('location.list.empty.action')}
								<Plus className="ml-2 h-4 w-4" />
							</Button>
						)}
					</div>
				</div>
			) : (
				<>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20 md:mb-0">
						{locations.data.map((location) => renderLocationCard(location))}
					</div>

					<PaginationControls
						currentPage={pagination.current}
						totalPages={locations.meta.pagination.total_pages}
						onPageChange={pagination.onChange}
						pageSize={pagination.pageSize}
						onPageSizeChange={handlePageSizeChange}
						className="pt-6"
					/>
				</>
			)}

			{canCreateLocation && (
				<button
					type="button"
					onClick={handleCreateLocation}
					className="md:hidden fixed bottom-[110px] right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-transparent shadow-lg transition hover:opacity-90"
					aria-label={t('location.list.createLocation')}
				>
					<span
						className="h-14 w-14 bg-no-repeat"
						style={{
							backgroundImage: `url(${addIcon})`,
							backgroundSize: '157px 209px',
							backgroundPosition: '-88px -48px',
						}}
					/>
				</button>
			)}

			<DeleteLocationModal
				visible={locationDeletion.isModalVisible}
				location={locationDeletion.selectedLocation}
				onClose={locationDeletion.closeDeleteModal}
				onConfirm={locationDeletion.handleDeleteLocation}
				isDeleting={locationDeletion.isDeleting}
			/>

			{/* <ShareLocationModal
				visible={locationShare.isModalVisible}
				currentStep={locationShare.currentStep}
				contactData={locationShare.originalContactData}
				isUpdating={locationShare.isUpdating}
				isRegenerating={locationShare.isRegenerating}
				isSharing={locationShare.isSharing}
				onClose={locationShare.closeShareModal}
				onNextStep={locationShare.nextStep}
				onRegenerateLink={locationShare.handleRegenerateLink}
				onCopyLink={locationShare.handleCopyLink}
				onShareLink={locationShare.handleShareLink}
			/> */}

			<UpdateLocationModal
				visible={locationUpdate.isModalVisible}
				location={locationUpdate.selectedLocation}
				onClose={locationUpdate.closeUpdateModal}
				onConfirm={locationUpdate.handleUpdateLocation}
				isUpdating={locationUpdate.isUpdating}
				mode="edit"
			/>

			<UpdateLocationModal
				visible={locationCreate.isModalVisible}
				location={null}
				onClose={locationCreate.closeCreateModal}
				onConfirm={locationCreate.handleCreateLocation}
				isUpdating={locationCreate.isCreating}
				mode="create"
			/>
		</div>
	)
}
